import React from "react";
import Image from "next/image";
import { getIconUrl } from "@/lib/directus";
import { LazyMotion, domAnimation, m } from "framer-motion";

interface ImageTextLightProps {
  data: ImageTextLightData;
}

export interface ImageTextLightData {
  sectionName: string;
  title: string;
  text: string;
  textAligement: "left" | "right";
  image: string;
}

const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function ImageTextLight({ data }: ImageTextLightProps) {
  const isRight = data.textAligement === "right";
  const sectionId = data.sectionName.replace(/\s+/g, "-");

  const textSection = (
    <div className="w-full desktop:w-full flex flex-col justify-center items-center px-6 md:px-12 desktop:px-16 py-12 md:py-16 desktop:py-36 ">
      <m.div
        className="w-full flex flex-col gap-6 desktop:gap-16"
        variants={fadeInVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <m.h2
          className="text-white text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop text-left md:text-center desktop:text-left break-words font-serif"
          variants={fadeInVariants}
        >
          {data.title}
        </m.h2>
        <m.p
          className="text-white text-paragraph-large"
          variants={fadeInVariants}
        >
          {data.text}
        </m.p>
      </m.div>
    </div>
  );

  const imageSection = (
    <div className="relative w-full h-full desktop:w-full flex items-center justify-center ">
      <Image
        src={getIconUrl(data.image)}
        alt="section-illustration"
        width={0}
        height={0}
        sizes="50vw"
        className="object-contain brightness-0 invert w-1/2"
        priority={false}
      />
    </div>
  );

  return (
    <LazyMotion features={domAnimation}>
      <section
      id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}
      data-name={data.sectionName}
      className="flex flex-col desktop:flex-row w-full bg-[#59697C]"
    >
      {/* Mobile/Tablet: Image first, then text */}
      <div className="flex flex-col desktop:hidden desktop:w-[85vw]">
        {imageSection}
        {textSection}
      </div>

      {/* Desktop: Respect alignment setting */}
      <div className="hidden desktop:flex desktop:flex-row w-full gap-[42px]">
        {isRight ? (
          <>
            <div style={{ width: "33.33%" }}>{imageSection}</div>
            <div style={{ width: "66.66%" }}>{textSection}</div>
          </>
        ) : (
          <>
            <div style={{ width: "66.66%" }}>{textSection}</div>
            <div style={{ width: "33.33%" }}>{imageSection}</div>
          </>
        )}
      </div>
    </section>
    </LazyMotion>
  );
}
